// SwapImeiDialog
// --------------
// Exchanges hardware identities between this device and another existing
// device. After confirm, both Device records keep their company / station
// / admins, but their IMEI + name + model + device_type + server config
// move to the other side. Powermeter and Configuration rows stay keyed by
// IMEI and therefore follow each device automatically; logs are NOT
// touched (caller-managed per the user's design).
//
// Target picker is a DeviceSearchAutocomplete — supports substring on
// IMEI/name/model and `/pattern/` regex on IMEI. The source device is
// excluded from results so it can't be picked as its own target.

import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import SwapIcon from "@mui/icons-material/SwapHorizOutlined";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  swapDeviceImei,
  fetchCompanyDevices,
} from "./companyDevicesSlice";
import { fetchAllDevices } from "../devices/devicesSlice";
import DeviceSearchAutocomplete from "./DeviceSearchAutocomplete";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../components/common/DangerConfirmField";

const TYPE_LABEL = { 1: "Motor", 2: "Tank", 3: "Valve", 4: "Flow" };

export default function SwapImeiDialog({ device, onClose, refreshScope }) {
  // `refreshScope` controls which list to refetch on success:
  //   - { kind: "company", companyId } → fetchCompanyDevices
  //   - { kind: "all" }                → fetchAllDevices
  const dispatch = useDispatch();
  const [target, setTarget] = useState(null);
  const [securityCode, setSecurityCode] = useState("");
  const [swapping, setSwapping] = useState(false);

  const sourceImei = device?.IMEI || "";

  const canSwap =
    Boolean(target) &&
    target.IMEI !== sourceImei &&
    isDangerCodeValid(securityCode) &&
    !swapping;

  const performSwap = async () => {
    if (!canSwap) return;
    setSwapping(true);
    const res = await dispatch(
      swapDeviceImei({ sourceImei, targetImei: target.IMEI })
    );
    setSwapping(false);
    if (swapDeviceImei.fulfilled.match(res)) {
      toast.success(`Swapped ${sourceImei} ↔ ${target.IMEI}`);
      if (refreshScope?.kind === "company" && refreshScope.companyId) {
        dispatch(fetchCompanyDevices({ companyId: refreshScope.companyId }));
      } else if (refreshScope?.kind === "all") {
        dispatch(fetchAllDevices());
      }
      onClose();
    } else {
      toast.error(res.payload || "Swap failed");
    }
  };

  return (
    <Dialog
      open
      onClose={swapping ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(135deg, #b45309 0%, #d97706 50%, #f59e0b 100%)",
          color: "common.white",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={swapping}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.22)",
              border: "2px solid rgba(255,255,255,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SwapIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
              }}
            >
              Swap IMEI
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", opacity: 0.92, mt: 0.25 }}
            >
              Exchange hardware identity with another device
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* Source — read-only summary */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
              fontSize: "0.6875rem",
              mb: 0.5,
            }}
          >
            This device
          </Typography>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.default",
            }}
          >
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
              <b>{sourceImei}</b>
              {device?.device_name && (
                <Typography component="span" sx={{ ml: 1, color: "text.secondary" }}>
                  · {device.device_name}
                </Typography>
              )}
              <Typography component="span" sx={{ ml: 1, color: "text.secondary", fontSize: "0.75rem" }}>
                ({TYPE_LABEL[device?.device_type] || "type?"})
              </Typography>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }}>
          <SwapIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        </Divider>

        {/* Target picker — autocomplete with regex + substring */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "text.secondary",
            fontSize: "0.6875rem",
            mb: 0.5,
          }}
        >
          Target device
        </Typography>
        <DeviceSearchAutocomplete
          value={target}
          onChange={(d) => {
            setTarget(d);
            setSecurityCode("");
          }}
          excludeIds={device?._id ? [device._id] : []}
          autoFocus
          disabled={swapping}
          helperText="Search by IMEI, name, or model. Use /…/ for regex on IMEI."
        />

        {target && target.IMEI === sourceImei && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography variant="caption">
              Pick a different device — can't swap with itself.
            </Typography>
          </Alert>
        )}

        {target && target.IMEI !== sourceImei && (
          <>
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1,
                backgroundColor: "warning.light",
                color: "warning.contrastText",
              }}
            >
              <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                After swap:
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                • This slot will hold IMEI <b>{target.IMEI}</b> with its
                tested Powermeter + Configuration grid.
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                • The other device (currently in{" "}
                <b>{target.company?.name || "— unassigned —"}</b>) will hold
                IMEI <b>{sourceImei}</b>.
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                • Companies / stations / admin assignments stay where they
                are. Logs are NOT moved.
              </Typography>
            </Box>
            <DangerConfirmField
              value={securityCode}
              onChange={setSecurityCode}
              disabled={swapping}
            />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={swapping}
          sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
        >
          Cancel
        </Button>
        <Button
          onClick={performSwap}
          variant="contained"
          color="warning"
          disabled={!canSwap}
          startIcon={
            swapping ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            borderRadius: "8px",
            flex: { xs: 1, sm: "0 0 auto" },
            boxShadow: "0 4px 10px -4px rgba(217,119,6,0.55)",
          }}
        >
          {swapping ? "Swapping…" : "Swap"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
